import { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, XCircle, PlayCircle, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure, azErr } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";

export default function AzLabs({ onGo }) {
  const { state, refresh } = useAzure();
  const labs = state?.labs || [];
  const [openId, setOpenId] = useState(labs[0]?.id);
  const [busy, setBusy] = useState(null);

  const validate = async (labId) => {
    setBusy(labId);
    try {
      const { data } = await api.post(`/azure/labs/${labId}/validate`);
      await refresh();
      if (data.completed) toast.success(`Lab complete! Score ${data.score}%`);
      else toast.message(`${data.passed}/${data.total} checks passed (${data.score}%)`);
    } catch (e) {
      toast.error(azErr(e));
    } finally {
      setBusy(null);
    }
  };

  const badge = (p) => {
    if (p.status === "completed") return <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Completed · {p.score}%</span>;
    if (p.status === "in_progress") return <span className="text-xs text-amber-600 font-medium">In progress · {p.score}%</span>;
    return <span className="text-xs text-muted-foreground">Not started</span>;
  };

  const goHint = (lab) => {
    const byId = {
      "LIC": "licensing", "MFA": "mfa", "CA": "conditional-access", "PIM": "pim", "IDP": "identity-protection",
    };
    const seg = lab.id.split("-")[1];
    if (byId[seg]) return onGo?.(byId[seg]);
    const map = {
      "Networking": lab.id.includes("007") ? "nsgs" : "vnets",
      "Compute": "vms",
      "Governance & RBAC": "rbac",
      "Microsoft Entra ID": lab.id.includes("002") || lab.id.includes("003") ? "entra-groups" : "entra-users",
    };
    onGo?.(map[lab.category] || "entra-users");
  };

  return (
    <div data-testid="az-labs" className="max-w-4xl">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap className="h-5 w-5 text-[#0078d4]" />
        <h2 className="font-display font-bold text-xl">Training Labs</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Hands-on, state-validated exercises. Complete the tasks in the portal, then run validation. Your environment persists between sessions.</p>

      <div className="space-y-3">
        {labs.map((lab) => {
          const p = lab.progress || {};
          const isOpen = openId === lab.id;
          return (
            <div key={lab.id} className="border border-border rounded-lg bg-card overflow-hidden" data-testid={`az-lab-${lab.id}`}>
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/50" onClick={() => setOpenId(isOpen ? null : lab.id)}>
                <span className="font-mono text-xs text-muted-foreground w-16">{lab.id.split("-").pop()}</span>
                <span className="font-semibold flex-1">{lab.title}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-muted">{lab.level}</span>
                {badge(p)}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 border-t border-border space-y-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Scenario</div>
                    <p className="text-sm">{lab.scenario}</p>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Requirements</div>
                    <ul className="text-sm list-disc pl-5 space-y-0.5">{lab.requirements.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Validation checks</div>
                    <div className="space-y-1.5">
                      {(p.tasks && p.tasks.length ? p.tasks : lab.tasks).map((t) => (
                        <div key={t.id} className="flex items-start gap-2 text-sm">
                          {"passed" in t ? (t.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />)
                            : <span className="h-4 w-4 rounded-full border border-muted-foreground/40 mt-0.5 shrink-0" />}
                          <span>{t.label}{t.detail ? <span className="text-muted-foreground"> — {t.detail}</span> : null}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="sm" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2" data-testid={`az-validate-${lab.id}`} disabled={busy === lab.id} onClick={() => validate(lab.id)}>
                      {busy === lab.id ? "Checking…" : <><Trophy className="h-4 w-4" /> Validate</>}
                    </Button>
                    <Button size="sm" variant="outline" className="gap-2" onClick={() => goHint(lab)}><PlayCircle className="h-4 w-4" /> Open the blade</Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
